import React, { useEffect } from 'react';
import AppHeader from '../../component/AppHeader/app-header';
import Stepper from '../../component/Stepper/Stepper';
import BillingForm from './components/BillingForm';
import OrderSummary from './components/OrderSummary';
import './Checkout.scss';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useStepper } from '../../hooks/useStepper';
import { useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setBillingType, setPlan } from '../../store/reducers/subscriptionSlice/subscriptionSlice';
import PageLoader from '../../component/PageLoader';
import useSubscription from '../../hooks/useSubscription';
import { usePricingPlans } from '../../hooks/usePricingPlans';
import useSubUrl from '../../hooks/useSubUrl';
import { CheckoutFormProvider } from './CheckoutFormContext';

const CheckoutPage = () => {
	const { steps, currentStep, goToStep } = useStepper();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [searchParams] = useSearchParams();
	const { suburl: suburlParam } = useParams();
	const planKey = searchParams.get('plan');
	const billing = searchParams.get('billing');
	const { selectedPlan } = useSelector((state) => state.subscription);
	const { isSubscriptionActive, isSubscriptionFree } = useSubscription();
	const suburl = useSubUrl();
	// Only need a plan lookup here if the page was opened/refreshed directly
	// via URL (no selectedPlan in Redux yet) — usePricingPlans reuses the
	// same cached fetch PricingModal/subscription-plan already trigger.
	const { plans, loading: plansLoading } = usePricingPlans({
		enabled: !selectedPlan && Boolean(planKey),
	});
	const restoring = !selectedPlan && Boolean(planKey) && plansLoading;

	// This page is reachable both as bare /checkout (brand-new user, no
	// workspace yet) and /user/:suburl/checkout (resubscribing user who
	// already has one). If we know a suburl but the URL doesn't show it
	// yet, move to the scoped URL — same query string, same everything —
	// so the address bar matches the rest of the app.
	useEffect(() => {
		if (suburl && !suburlParam) {
			navigate(`/user/${suburl}/checkout?${searchParams.toString()}`, { replace: true });
		}
	}, [suburl, suburlParam, searchParams, navigate]);

	useEffect(() => {
		// Only bounce users who already hold a PAID active plan. A Free
		// subscription is stored with status 'active', so is_active is true for
		// Free users too — guarding on it alone kicked them straight back to
		// the subscription settings page, which looked like the page reloading
		// instead of opening checkout. Free users must be able to reach
		// checkout; that is the only way to upgrade.
		if (isSubscriptionActive && !isSubscriptionFree) {
			// An already-subscribed user with a real workspace shouldn't be
			// bounced through the generic, subdomain-less /payment-success
			// page — send them straight to their actual in-app URL, same as
			// every other authenticated page in the app. Only fall back to
			// /payment-success for the brand-new-user case where no
			// subdomain has been assigned yet.
			navigate(suburl ? `/user/${suburl}/settings/my-subscription` : '/payment-success', {
				replace: true,
			});
			return;
		}

		if (!selectedPlan && !planKey) {
			navigate(suburl ? `/user/${suburl}/subscription-plan` : '/subscription-plan', {
				replace: true,
			});
		}
	}, [isSubscriptionActive, isSubscriptionFree, selectedPlan, planKey, suburl, navigate]);

	useEffect(() => {
		if (selectedPlan || !planKey || !plans.length) return;

		const plan = plans.find((item) => item.key === planKey);
		if (!plan) return;

		dispatch(
			setPlan({
				id: plan._id,
				key: plan.key,
				title: plan.name,
				description: plan.meta?.description,
				billing: plan.billing || [],
			}),
		);

		dispatch(setBillingType(billing || 'monthly'));
	}, [planKey, billing, selectedPlan, plans, dispatch]);

	if (restoring) {
		return <PageLoader />;
	}

	return (
		<>
			<AppHeader title="Complete Checkout" />
			<main className="main-wrapper">
				<div className="pb-4">
					<Stepper
						steps={steps}
						currentStep={currentStep}
						onStepClick={goToStep}
					/>
				</div>
				<div className="pb-5">
					<div className="checkout-container">
						<CheckoutFormProvider>
							<div className="checkout-grid">
								<div className="checkout-left">
									<BillingForm />
								</div>

								<div className="checkout-right">
									<OrderSummary />
								</div>
							</div>
						</CheckoutFormProvider>
					</div>
				</div>
			</main>
		</>
	);
};

export default CheckoutPage;
